import React, { useState } from 'react';
import styles from './ReviewMetricsQuestion.module.scss';
import {
  Button,
  createToast,
  FormControl,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  RadioButton,
  RadioGroup,
  Text,
  Toast,
} from 'components/core';
import { Stack } from 'components/layout';
import { createReviewMetric } from 'query/review/ReviewMetric';

type EnumReviewMetricAnswer =
  | 'not_much'
  | 'some_time_saved'
  | 'noticeable_difference'
  | 'big_time_saved'
  | 'huge_time_saved'
  | 'na';

type Props = {
  showModal: boolean;
};

export const ReviewMetricsQuestion = ({ showModal }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(showModal);
  const [isLoading, setIsLoading] = useState(false);
  const [isMissingAnswer, setIsMissingAnswer] = useState(false);
  const [
    reviewMetricAnswer,
    setReviewMetricAnswer,
  ] = useState<EnumReviewMetricAnswer>('na');
  const labelId = 'review-metric-time-saved';
  const answers: EnumReviewMetricAnswer[] = [
    'not_much',
    'some_time_saved',
    'noticeable_difference',
    'big_time_saved',
    'huge_time_saved',
  ];

  const displayName = (key: EnumReviewMetricAnswer): string => {
    const displayNames: Record<string, string> = {
      not_much: 'Not much',
      some_time_saved: 'Some time saved',
      noticeable_difference: 'Noticeable difference',
      big_time_saved: 'Big time saved',
      huge_time_saved: 'Huge time saved',
    };

    const name = displayNames[key];

    if (!name) return '';

    return name;
  };

  const displayPercentage = (key: EnumReviewMetricAnswer): string => {
    const displayNames: Record<string, string> = {
      not_much: '(0% - 20%)',
      some_time_saved: '(20% - 40%)',
      noticeable_difference: '(40% - 60%)',
      big_time_saved: '(60% - 80%)',
      huge_time_saved: '(Over 80%)',
    };

    const name = displayNames[key];

    if (!name) return '';

    return name;
  };

  const renderRadioButton = (answer: EnumReviewMetricAnswer) => (
    <RadioButton key={answer} value={answer}>
      <Text variant="neutral">
        {displayName(answer)}
        <Text
          variant="weak"
          component="span"
          className={styles.percentageDescription}
        >
          {displayPercentage(answer)}
        </Text>
      </Text>
    </RadioButton>
  );

  const handleAnswerChecked = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setReviewMetricAnswer(e.target.value as EnumReviewMetricAnswer);
  };

  const handleSubmitAnswer = async () => {
    // returning if default 'na' is the value because if user closes the modal,
    // we'll send 'na' as the answer
    if (reviewMetricAnswer === 'na') {
      setIsMissingAnswer(true);
      return;
    }

    await submitAnswer();
  };

  const submitAnswer = async () => {
    setIsLoading(true);

    createReviewMetric(reviewMetricAnswer.toString())
      .then(() => {
        setIsModalOpen(false);
        setIsLoading(false);
      })
      .catch(() => {
        createToast(
          <Toast type="danger">
            Unable to save review metric, please try again later
          </Toast>
        );
      });
  };

  return (
    <Modal
      id="review-metric-time-saved-modal"
      isOpen={isModalOpen}
      onClose={submitAnswer}
    >
      <ModalBody>
        <Heading type="h4" id={labelId} className={styles.title}>
          For this review, how much time will your team save by using Covidence?
        </Heading>
        <Stack spacing={5}>
          <Text size="md" variant="weak">
            Compared to using a manual or non-Covidence process.
          </Text>
          <FormControl
            error={isMissingAnswer ? 'Please select one option' : undefined}
          >
            <RadioGroup
              name="review-metric-time-saved"
              ariaLabelledBy={labelId}
              defaultValue={reviewMetricAnswer}
              onChange={handleAnswerChecked}
            >
              {answers.map(renderRadioButton)}
            </RadioGroup>
          </FormControl>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          size="sm"
          variant="solid"
          type="brand"
          htmlType="submit"
          isLoading={isLoading}
          onClick={handleSubmitAnswer}
        >
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};
